/**
 * Step 3: Media selection.
 * Uses variantThumbs to show correct aspect ratio (1:1 or 9:16) based on formatType.
 * Includes inline AI-image generation (Gemini Nano Banana) per-company.
 */
import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Check, Sparkles, Loader2, ImagePlus, Video, Play } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || String(x)).join("; ") || fallback;
  return fallback;
};

const ASPECT_MAP = {
  instagram_story: { ratio: "9 / 16", label: "9:16" },
  instagram_feed_4_5: { ratio: "4 / 5", label: "4:5" },
  instagram_post: { ratio: "1 / 1", label: "1:1" },
};

const getAspectRatio = (variantKey) => ASPECT_MAP[variantKey]?.ratio || "1 / 1";
const getAspectLabel = (variantKey) => ASPECT_MAP[variantKey]?.label || "1:1";

const PERSPECTIVES = [
  "Nahaufnahme (Detail)", "Weitwinkel / Totale", "Frontalansicht", "Seitenansicht",
  "Von oben (Drohne)", "Innenansicht", "Lifestyle mit Person", "Studio / cleaner Hintergrund",
];

function PromptAssist({ kind, perspective, setPerspective, refine, setRefine, suggesting, onSuggest, onRefine, hasPrompt }) {
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Select value={perspective || "none"} onValueChange={(v) => setPerspective(v === "none" ? "" : v)}>
          <SelectTrigger className="flex-1 h-9 text-xs" data-testid={`ai-${kind}-perspective`}>
            <SelectValue placeholder="Blickwinkel (optional)" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Blickwinkel: egal</SelectItem>
            {PERSPECTIVES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          className="h-9 text-xs shrink-0 border-[#2563EB]/40 text-[#2563EB]"
          onClick={onSuggest}
          disabled={suggesting}
          data-testid={`ai-${kind}-suggest-btn`}
        >
          {suggesting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
          Prompt vorschlagen
        </Button>
      </div>
      {hasPrompt && (
        <div className="flex gap-2">
          <Input
            value={refine}
            onChange={(e) => setRefine(e.target.value)}
            placeholder="Aenderungswunsch, z.B. 'abends, mehr Nahaufnahme'"
            className="flex-1 h-9 text-xs"
            data-testid={`ai-${kind}-refine-input`}
          />
          <Button
            size="sm"
            variant="outline"
            className="h-9 text-xs shrink-0"
            onClick={onRefine}
            disabled={suggesting || !refine.trim()}
            data-testid={`ai-${kind}-refine-btn`}
          >
            Anpassen
          </Button>
        </div>
      )}
    </div>
  );
}

export default function StepMedia({
  media, selectedMedia, toggleMedia,
  thumbnails, variantThumbs, currentVariantKey, t,
  companyId, onMediaReload, platform,
}) {
  const isTikTok = platform === "tiktok";
  const images = media.filter((m) => isTikTok
    ? m.content_type?.startsWith("video/")
    : (m.content_type?.startsWith("image/") || m.content_type?.startsWith("video/")));
  const [dialogOpen, setDialogOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);

  // Sora-2 Video state
  const [videoOpen, setVideoOpen] = useState(false);
  const [vPrompt, setVPrompt] = useState("");
  const [vSize, setVSize] = useState("720x1280");
  const [vDuration, setVDuration] = useState("8");
  const [vModel, setVModel] = useState("veo-3.1");
  const [vRefId, setVRefId] = useState("");
  const [iPerspective, setIPerspective] = useState("");
  const [vPerspective, setVPerspective] = useState("");
  const [iRefine, setIRefine] = useState("");
  const [vRefine, setVRefine] = useState("");
  const [suggesting, setSuggesting] = useState(false);

  const suggestPrompt = async (kind, refine = false) => {
    if (!companyId) return;
    setSuggesting(true);
    try {
      const curr = kind === "video" ? vPrompt : prompt;
      const res = await axios.post(
        `${API}/ai/suggest-media-prompt`,
        {
          company_id: companyId,
          kind,
          perspective: (kind === "video" ? vPerspective : iPerspective) || undefined,
          ...(kind === "video" ? { duration: parseInt(vDuration) || 8 } : {}),
          ...(kind === "video" && vRefId ? { template_media_id: vRefId } : {}),
          ...(refine ? { refine_text: kind === "video" ? vRefine : iRefine, current_prompt: curr } : {}),
        },
        { withCredentials: true }
      );
      const p = res.data?.prompt || "";
      if (kind === "video") { setVPrompt(p); setVRefine(""); }
      else { setPrompt(p); setIRefine(""); }
    } catch (e) {
      toast.error(errText(e, "Prompt-Vorschlag fehlgeschlagen"));
    }
    setSuggesting(false);
  };
  const [vJob, setVJob] = useState(null);
  const [vStatus, setVStatus] = useState("idle"); // kept minimal: idle | starting
  const pollRef = useRef(null);
  const [bgJobs, setBgJobs] = useState([]);
  const [nowTs, setNowTs] = useState(Date.now());
  const [previewVideo, setPreviewVideo] = useState(null); // {url, name}

  // Resume running jobs (after reload / dialog closed)
  useEffect(() => {
    if (!companyId) return;
    axios.get(`${API}/ai/video-jobs?company_id=${companyId}&active=1`, { withCredentials: true })
      .then((r) => setBgJobs(r.data.items || []))
      .catch(() => {});
  }, [companyId]);

  // Elapsed-time ticker while jobs run
  useEffect(() => {
    if (bgJobs.length === 0) return;
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [bgJobs.length]);

  // Background polling of all running jobs
  useEffect(() => {
    if (bgJobs.length === 0) return;
    const iv = setInterval(async () => {
      for (const job of bgJobs) {
        try {
          const r = await axios.get(`${API}/ai/generate-video/${job.id}`, { withCredentials: true });
          if (r.data.status === "done") {
            toast.success("Video fertig und zu Medien hinzugefuegt!");
            setBgJobs((p) => p.filter((j) => j.id !== job.id));
            if (onMediaReload) await onMediaReload();
            if (r.data.media_id) toggleMedia(r.data.media_id);
          } else if (r.data.status === "failed") {
            toast.error("Video-Job fehlgeschlagen: " + (r.data.error || "Unbekannt"));
            setBgJobs((p) => p.filter((j) => j.id !== job.id));
          }
        } catch (e) { /* keep polling */ }
      }
    }, 8000);
    return () => clearInterval(iv);
  }, [bgJobs, onMediaReload, toggleMedia]);

  const fmtElapsed = (iso) => {
    const s = Math.max(0, Math.floor((nowTs - new Date(iso).getTime()) / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  };

  const generate = async () => {
    if (!prompt.trim() || !companyId) return;
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/ai/generate-image`,
        { prompt: prompt.trim(), company_id: companyId, category: "ai-generated" },
        { withCredentials: true }
      );
      toast.success("KI-Bild erstellt und zu Medien hinzugefuegt");
      setDialogOpen(false);
      setPrompt("");
      if (onMediaReload) await onMediaReload();
      if (res.data?.id) toggleMedia(res.data.id); // auto-select
    } catch (e) {
      toast.error(errText(e, "Bildgenerierung fehlgeschlagen"));
    }
    setGenerating(false);
  };

  // Start video job in the BACKGROUND: dialog closes, progress chip keeps tracking
  const startVideo = async () => {
    if (!vPrompt.trim() || !companyId) return;
    setVStatus("starting");
    try {
      const d = parseInt(vDuration) || 8;
      const isVeo = vModel === "veo-3.1";
      const res = await axios.post(
        `${API}/ai/generate-video`,
        {
          prompt: vPrompt.trim(),
          company_id: companyId,
          size: vSize,
          duration: isVeo && d > 8 ? 8 : d,
          ...(isVeo && d > 8 ? { target_duration: d } : {}),
          model: vModel,
          ...(vRefId ? { template_media_id: vRefId } : {}),
        },
        { withCredentials: true }
      );
      setBgJobs((p) => [...p, res.data]);
      setVideoOpen(false);
      setVPrompt("");
      setVRefId("");
      toast.info("Video wird im Hintergrund erstellt — Fortschritt siehst du oben im Medien-Schritt.");
    } catch (e) {
      toast.error(errText(e, "Video-Start fehlgeschlagen"));
    }
    setVStatus("idle");
  };

  return (
    <Card className="border-[var(--studio-border)] shadow-none animate-fade-in">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading">{t("content.media_title")}</CardTitle>
          <div className="flex items-center gap-2">
            {bgJobs.map((job) => (
              <span
                key={job.id}
                className="flex items-center gap-1.5 text-[10px] bg-[#2563EB]/10 text-[#2563EB] px-2 py-1 rounded-full font-semibold"
                data-testid={`video-progress-chip-${job.id}`}
              >
                <Loader2 className="w-3 h-3 animate-spin" />
                Video laeuft… {fmtElapsed(job.created_at)}
              </span>
            ))}
            {currentVariantKey && (
              <span
                className="text-[10px] bg-[#2563EB]/10 text-[#2563EB] px-2 py-0.5 rounded-full font-mono font-semibold"
                data-testid="wizard-aspect-hint"
              >
                Vorschau {getAspectLabel(currentVariantKey)} (auto-crop)
              </span>
            )}
            {!isTikTok && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => setDialogOpen(true)}
                data-testid="open-ai-image-btn"
              >
                <Sparkles className="w-3 h-3 mr-1 text-[#2563EB]" /> KI-Bild erzeugen
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={() => setVideoOpen(true)}
              data-testid="open-ai-video-btn"
            >
              <Video className="w-3 h-3 mr-1 text-[#2563EB]" /> KI-Video
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isTikTok && (
          <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-3" data-testid="tiktok-video-only-hint">
            🎬 TikTok erlaubt NUR Videos (MP4/MOV) — Bilder sind hier ausgeblendet. Nutze «KI-Video» oder lade ein Video in die Mediathek.
          </p>
        )}
        {images.length === 0 ? (
          <div className="text-sm text-[var(--studio-text-muted)] space-y-2">
            <p>{isTikTok ? "Keine Videos fuer diese Firma. Erzeuge eines mit KI-Video oder lade ein MP4/MOV hoch." : "Keine Medien fuer diese Firma. Lade zuerst Bilder hoch — oder erzeuge eines mit KI."}</p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => (isTikTok ? setVideoOpen(true) : setDialogOpen(true))}
              data-testid="empty-ai-image-btn"
            >
              {isTikTok ? <Video className="w-4 h-4 mr-1" /> : <ImagePlus className="w-4 h-4 mr-1" />}
              {isTikTok ? "KI-Video erzeugen" : "KI-Bild erzeugen"}
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-3">
            {images.map((m) => {
              const variantThumb = currentVariantKey && variantThumbs[m.id]?.[currentVariantKey];
              const hasVariant = currentVariantKey && m.variants?.[currentVariantKey];
              const previewSrc = variantThumb || thumbnails[m.id];
              const isSelected = selectedMedia.includes(m.id);
              const isVideo = m.content_type?.startsWith("video/");
              return (
                <button
                  key={m.id}
                  onClick={() => toggleMedia(m.id)}
                  data-testid={`select-media-${m.id}`}
                  className={`relative rounded-lg border-2 overflow-hidden transition-all ${
                    isSelected
                      ? "border-[#2563EB] ring-2 ring-[#2563EB]/30"
                      : "border-transparent hover:border-gray-300"
                  }`}
                  style={{ aspectRatio: currentVariantKey ? getAspectRatio(currentVariantKey) : "1 / 1" }}
                >
                  {isVideo ? (
                    thumbnails[m.id] ? (
                      <div className="relative w-full h-full">
                        <img src={thumbnails[m.id]} alt={m.original_filename} className="w-full h-full object-cover" />
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); setPreviewVideo({ url: `${API}/media/file/${m.storage_path}`, name: m.original_filename }); }}
                          className="absolute bottom-1 right-1 w-7 h-7 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center cursor-pointer"
                          title="Video ansehen"
                          data-testid={`preview-video-${m.id}`}
                        >
                          <Play className="w-3.5 h-3.5 text-white ml-0.5" />
                        </span>
                        {m.is_ai_generated && (m.metadata?.model === "veo-3.1") && ((m.metadata?.duration || 8) <= 29) && (
                          <span
                            role="button"
                            tabIndex={0}
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Video um +7 Sekunden verlängern (~$2.80, dauert 2-5 Min)?`)) return;
                              try {
                                const r = await axios.post(`${API}/ai/extend-video`, { media_id: m.id }, { withCredentials: true });
                                toast.info(r.data.message);
                              } catch (err) { toast.error(err.response?.data?.detail || "Verlängerung fehlgeschlagen"); }
                            }}
                            className="absolute bottom-1 left-1 px-1.5 h-7 rounded-full bg-black/60 hover:bg-black/80 flex items-center justify-center cursor-pointer text-[10px] text-white font-semibold"
                            title="Mit KI um +7 Sekunden verlängern (Veo)"
                            data-testid={`extend-video-${m.id}`}
                          >
                            +7s
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-[#1a1a1a] to-[#0F0F0F] flex items-center justify-center">
                        <Loader2 className="w-6 h-6 text-white/50 animate-spin" />
                      </div>
                    )
                  ) : previewSrc ? (
                    <img src={previewSrc} alt={m.original_filename} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center text-xs text-gray-400">
                      Bild
                    </div>
                  )}
                  {hasVariant && (
                    <span
                      className="absolute bottom-1 left-1 text-[9px] bg-[#0F0F0F]/80 text-white px-1.5 py-0.5 rounded font-mono font-semibold"
                      data-testid={`media-variant-tag-${m.id}`}
                    >
                      {getAspectLabel(currentVariantKey)}
                    </span>
                  )}
                  {m.is_ai_generated && (
                    <span
                      className="absolute top-1 left-1 text-[9px] bg-[#2563EB] text-white px-1.5 py-0.5 rounded font-mono font-semibold flex items-center gap-0.5"
                      data-testid={`media-ai-tag-${m.id}`}
                    >
                      <Sparkles className="w-2.5 h-2.5" /> KI
                    </span>
                  )}
                  {m.note && (
                    <span
                      className="absolute top-1 right-1 text-[9px] bg-amber-500/90 text-white px-1.5 py-0.5 rounded font-semibold max-w-[85%] truncate"
                      data-testid={`media-note-tag-${m.id}`}
                      title={m.note}
                    >
                      {m.note}
                    </span>
                  )}
                  {isSelected && (
                    <div className="absolute inset-0 bg-[#2563EB]/20 flex items-center justify-center">
                      <Check className="w-6 h-6 text-[#2563EB]" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>        <DialogContent className="sm:max-w-lg" data-testid="ai-image-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#2563EB]" /> KI-Bild erzeugen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-[var(--studio-text-muted)]">
              Beschreibe dein Wunschbild — oder lass die KI einen Vorschlag machen (sie kennt das Markenprofil deiner Firma).
            </p>
            <PromptAssist
              kind="image"
              perspective={iPerspective} setPerspective={setIPerspective}
              refine={iRefine} setRefine={setIRefine}
              suggesting={suggesting}
              onSuggest={() => suggestPrompt("image")}
              onRefine={() => suggestPrompt("image", true)}
              hasPrompt={!!prompt.trim()}
            />
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="z.B. Sonniger Kaffeetisch in Zuerich mit frischen Croissants, warme Morgenlicht, fotorealistisch, Instagram-Feed-Style"
              rows={5}
              data-testid="ai-image-prompt"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDialogOpen(false)}
              disabled={generating}
              data-testid="ai-image-cancel"
            >
              Abbrechen
            </Button>
            <Button
              onClick={generate}
              disabled={generating || !prompt.trim()}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="ai-image-generate-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Bild generieren
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sora-2 Video Dialog */}
      <Dialog open={videoOpen} onOpenChange={(v) => { if (!v) { setVideoOpen(false); } }}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-video-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Video className="w-5 h-5 text-[#2563EB]" /> KI-Video erzeugen
            </DialogTitle>
          </DialogHeader>
          {vStatus === "starting" ? (
            <div className="py-8 text-center space-y-3" data-testid="video-running-state">
              <Loader2 className="w-10 h-10 animate-spin mx-auto text-[#2563EB]" />
              <p className="text-sm font-medium">Video-Job wird gestartet…</p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-[var(--studio-text-muted)]">
                Beschreibe dein Video — oder lass die KI einen Vorschlag machen. Empfohlen fuer Reels/TikTok: Portrait 9:16, 8 Sekunden.
              </p>
              <PromptAssist
                kind="video"
                perspective={vPerspective} setPerspective={setVPerspective}
                refine={vRefine} setRefine={setVRefine}
                suggesting={suggesting}
                onSuggest={() => suggestPrompt("video")}
                onRefine={() => suggestPrompt("video", true)}
                hasPrompt={!!vPrompt.trim()}
              />
              <Textarea
                value={vPrompt}
                onChange={(e) => setVPrompt(e.target.value)}
                placeholder="z.B. Barista giesst Latte-Art in Zeitlupe, warme Beleuchtung, Cafeteria-Ambiente"
                rows={4}
                data-testid="ai-video-prompt"
              />
              <div>
                <label className="text-xs text-[var(--studio-text-muted)]">Modell</label>
                <Select value={vModel} onValueChange={setVModel}>
                  <SelectTrigger className="mt-1" data-testid="ai-video-model"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="veo-3.1">Veo 3.1 (Google) — beste Qualitaet, mit Ton</SelectItem>
                    <SelectItem value="runway-gen4.5">Runway Gen-4.5 — stark fuer Produktvideos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-[var(--studio-text-muted)]">Format</label>
                  <Select value={vSize} onValueChange={setVSize}>
                    <SelectTrigger className="mt-1" data-testid="ai-video-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="720x1280">Portrait 9:16 (Reel/TikTok)</SelectItem>
                      <SelectItem value="1280x720">Landscape 16:9</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-[var(--studio-text-muted)]">Dauer</label>
                  <Select value={vDuration} onValueChange={setVDuration}>
                    <SelectTrigger className="mt-1" data-testid="ai-video-duration"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {vModel === "veo-3.1" && <SelectItem value="4">4 Sekunden</SelectItem>}
                      {vModel === "runway-gen4.5" && <SelectItem value="5">5 Sekunden</SelectItem>}
                      {vModel === "veo-3.1" && <SelectItem value="6">6 Sekunden</SelectItem>}
                      <SelectItem value="8">8 Sekunden</SelectItem>
                      {vModel === "runway-gen4.5" && <SelectItem value="10">10 Sekunden</SelectItem>}
                      {vModel === "veo-3.1" && <SelectItem value="15">15 Sek. (Auto-Verlängerung)</SelectItem>}
                      {vModel === "veo-3.1" && <SelectItem value="22">22 Sek. (Auto-Verlängerung)</SelectItem>}
                      {vModel === "veo-3.1" && <SelectItem value="29">29 Sek. (Auto-Verlängerung)</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {vModel === "veo-3.1" && vRefId && vDuration !== "8" && parseInt(vDuration) < 9 && (
                <p className="text-[10px] text-amber-600">Hinweis: Bei Veo mit Vorlagen-Bild betraegt die Dauer immer 8 Sekunden.</p>
              )}
              <p className="text-[11px] text-[var(--studio-text-muted)]" data-testid="ai-video-cost-estimate">
                💰 Geschätzte Kosten: <b>~${(() => {
                  const d = parseInt(vDuration) || 8;
                  if (vModel === "runway-gen4.5") return (d * 0.05).toFixed(2);
                  const base = Math.min(d, 8) * 0.4;
                  const ext = d > 8 ? Math.ceil((d - 8) / 7) * 2.8 : 0;
                  return (base + ext).toFixed(2);
                })()}</b> (dein API-Konto{parseInt(vDuration) > 8 ? " · inkl. Auto-Verlängerungen à +7s" : ""})
              </p>
              {media.filter((m) => m.content_type?.startsWith("image/")).length > 0 && (
                <div>
                  <label className="text-xs text-[var(--studio-text-muted)]">
                    Eigenes Bild als Vorlage (optional) — die KI animiert dein Bild zum Video
                  </label>
                  <p className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 mt-1">
                    ⭐ Empfohlen für Logos (z.B. Fiat): Wähle ein echtes Foto als Startbild — das Video beginnt exakt damit, Logos und Schriftzüge bleiben 100% korrekt.
                  </p>
                  {(() => {
                    const [rw, rh] = vSize.split("x").map(Number);
                    const ratio = `${rw} / ${rh}`;
                    const tileH = rh >= rw ? 80 : 56;
                    return (
                      <div className="mt-1 flex gap-2 overflow-x-auto pb-1 items-start" data-testid="ai-video-ref-strip">
                        <button
                          type="button"
                          onClick={() => setVRefId("")}
                          style={{ aspectRatio: ratio, height: tileH }}
                          className={`shrink-0 rounded border text-[10px] text-[var(--studio-text-muted)] flex items-center justify-center text-center px-1 ${!vRefId ? "border-[#2563EB] ring-2 ring-[#2563EB]/30" : "border-[var(--studio-border)]"}`}
                          data-testid="ai-video-ref-none"
                        >
                          Ohne Vorlage
                        </button>
                        {media.filter((m) => m.content_type?.startsWith("image/")).map((m) => (
                          <button
                            key={m.id}
                            type="button"
                            onClick={() => setVRefId(vRefId === m.id ? "" : m.id)}
                            style={{ aspectRatio: ratio, height: tileH }}
                            className={`shrink-0 rounded border overflow-hidden ${vRefId === m.id ? "border-[#2563EB] ring-2 ring-[#2563EB]/30" : "border-[var(--studio-border)]"}`}
                            title={m.original_filename}
                            data-testid={`ai-video-ref-${m.id}`}
                          >
                            {thumbnails[m.id] ? (
                              <img src={thumbnails[m.id]} alt={m.original_filename} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-[var(--studio-bg)]" />
                            )}
                          </button>
                        ))}
                      </div>
                    );
                  })()}
                  <p className="text-[10px] text-[var(--studio-text-muted)] mt-1">
                    Die Vorschau zeigt den Zuschnitt im gewaehlten Format. Tipp: Quer-Fotos passen am besten zu Landscape 16:9, Hochformat-Fotos zu Portrait 9:16.
                  </p>
                  {vRefId && (
                    <p className="text-[10px] text-[#2563EB] mt-1">Das gewaehlte Bild wird als visueller Startpunkt des Videos verwendet.</p>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setVideoOpen(false); }}
              disabled={vStatus === "starting"}
              data-testid="ai-video-cancel"
            >
              Abbrechen
            </Button>
            <Button
              onClick={startVideo}
              disabled={!vPrompt.trim() || vStatus === "starting"}
              className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
              data-testid="ai-video-start-btn"
            >
              <Video className="w-4 h-4 mr-2" /> Video starten
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video preview before posting */}
      <Dialog open={!!previewVideo} onOpenChange={(v) => { if (!v) setPreviewVideo(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="video-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Play className="w-5 h-5 text-[#2563EB]" /> Video-Vorschau
            </DialogTitle>
          </DialogHeader>
          {previewVideo && (
            <video
              src={previewVideo.url}
              controls
              autoPlay
              playsInline
              className="w-full max-h-[60vh] rounded-lg bg-black"
              data-testid="video-preview-player"
            />
          )}
          <p className="text-[11px] text-[var(--studio-text-muted)] truncate">{previewVideo?.name}</p>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
